/**
 * The Queue screen - the tracking view the capture flow reports into.
 *
 * It answers the one question the till screen cannot: *where is each receipt
 * right now, and can the paper go?* Four states, four sections, each with the
 * photo so an accidental capture is recognisable and removable:
 *
 *   on this phone      IndexedDB, not yet uploaded - keep the paper
 *   on the server      uploaded, waiting for a run - the paper can go
 *   needs your answer  held by a run with a question
 *   in the ledger      processed; photo may already be pruned
 *
 * Discarding is available in every removable state: a local item is deleted
 * from IndexedDB before it ever uploads; a server item goes through
 * DELETE /api/captures, which prunes the photo bytes and is never resurrected
 * by a publish (the server records it as a human decision).
 */
import { useEffect, useState } from "preact/hooks";

import * as api from "./api.js";
import { dateName, money } from "./format.js";
import { photoBlob, type QueueItem } from "./queue.js";
import * as store from "./store.js";
import { Lightbox } from "./widgets.js";

export interface QueueActions {
  discardLocal: (captureId: string) => Promise<void> | void;
  retryLocal: (captureId: string) => Promise<void> | void;
  discardServer: (captureId: string) => Promise<void> | void;
  answerServer: (captureId: string, answer: string) => Promise<void> | void;
  /** Add/correct the terminal figure on an uploaded, still-pending capture. */
  setServerTotal: (captureId: string, cardTotal: number | null) => Promise<void> | void;
  /** Same, for a capture still on this phone. */
  setLocalTotal: (captureId: string, cardTotal: number | null) => Promise<void> | void;
}

/**
 * The card-total field on a queue row. The R21 reconciliation - the one check
 * built on a figure the agent did not produce - only exists when this number
 * does, and the till screen deliberately never demands it. This is the calm
 * second chance: receipt in hand, one field, done.
 */
function TotalField({
  value,
  currency,
  onSave,
}: {
  value: number | null;
  currency: string;
  onSave: (v: number | null) => void | Promise<void>;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");

  if (!editing) {
    return (
      <button type="button" class="link totaledit" onClick={() => {
        setDraft(value === null ? "" : String(value));
        setEditing(true);
      }}>
        {value === null ? "+ add card total" : `card total ${money(value, currency)} - edit`}
      </button>
    );
  }
  return (
    <form
      class="totalform"
      onSubmit={(e) => {
        e.preventDefault();
        const v = draft.trim() === "" ? null : Number(draft);
        if (v !== null && (!Number.isFinite(v) || v <= 0)) return;
        void onSave(v);
        setEditing(false);
      }}
    >
      <input
        type="text"
        inputmode="decimal"
        placeholder="what the terminal charged"
        aria-label="Card total"
        value={draft}
        onInput={(e) => setDraft((e.target as HTMLInputElement).value.replace(/[^\d.]/g, ""))}
      />
      <button type="submit">Save</button>
      <button type="button" onClick={() => setEditing(false)}>Cancel</button>
    </form>
  );
}

/** A thumbnail that opens the full photo when there is one to open. */
function Thumb({ url, onView }: { url: string | null; onView: (url: string) => void }) {
  if (!url) return <span class="qthumb ph" />;
  return (
    <button type="button" class="qthumbbtn" aria-label="View photo full screen" onClick={() => onView(url)}>
      <img class="qthumb" src={url} alt="" />
    </button>
  );
}

/** A locally queued photo, straight from the bytes IndexedDB holds. */
function LocalThumb({ item, onView }: { item: QueueItem; onView: (url: string) => void }) {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (typeof URL.createObjectURL !== "function") return;
    const u = URL.createObjectURL(photoBlob(item));
    setUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [item.capture_id]);
  return <Thumb url={url} onView={onView} />;
}

/** A server-side photo, via the authenticated fetch + Cache API. */
function ServerThumb({
  captureId,
  available,
  onView,
}: {
  captureId: string;
  available: boolean;
  onView: (url: string) => void;
}) {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!available) return;
    let revoke: string | null = null;
    let active = true;
    store
      .photoBlob(captureId, api.capturePhoto)
      .then((blob) => {
        if (!active) return;
        revoke = URL.createObjectURL(blob);
        setUrl(revoke);
      })
      .catch(() => undefined); // pruned or offline: placeholder, honestly
    return () => {
      active = false;
      if (revoke) URL.revokeObjectURL(revoke);
    };
  }, [captureId, available]);
  return <Thumb url={url} onView={onView} />;
}

function when(iso: string): string {
  const d = iso.slice(0, 10);
  const t = iso.slice(11, 16);
  return `${dateName(d)} ${t}`;
}

export function QueueScreen({
  local,
  server,
  actions,
}: {
  local: QueueItem[];
  server: api.CaptureRow[];
  actions: QueueActions;
}) {
  const [view, setView] = useState<string | null>(null);
  const pending = server.filter((c) => c.status === "pending");
  const held = server.filter((c) => c.status === "held");
  const processed = server.filter((c) => c.status === "processed").slice(0, 10);

  const empty = !local.length && !pending.length && !held.length && !processed.length;
  if (empty) {
    return (
      <div class="queuescreen empty-state">
        <h2>Nothing in the queue</h2>
        <p class="muted">Receipts you photograph appear here on their way to the ledger.</p>
      </div>
    );
  }

  const discardServer = (id: string) => {
    if (confirm("Discard this receipt? The photo is deleted from the server and this cannot be undone.")) {
      void actions.discardServer(id);
    }
  };

  return (
    <div class="queuescreen">
      {view && <Lightbox url={view} onClose={() => setView(null)} />}
      {local.length > 0 && (
        <section>
          <h3>On this phone - keep the paper</h3>
          <ul class="qrows">
            {local.map((i) => (
              <li key={i.capture_id} class={`qrow ${i.state}`}>
                <LocalThumb item={i} onView={setView} />
                <div class="qmain">
                  <span class="qtitle">{i.sidecar.store}</span>
                  <span class="qsub muted small">
                    {when(i.sidecar.captured_at)}
                    {" · "}
                    {i.state === "failed" ? `failed: ${i.error_code ?? "error"}` : i.state}
                  </span>
                  <TotalField
                    value={i.sidecar.card_total}
                    currency={i.sidecar.currency}
                    onSave={(v) => actions.setLocalTotal(i.capture_id, v)}
                  />
                </div>
                <span class="qactions">
                  {i.state === "failed" && (
                    <button type="button" onClick={() => void actions.retryLocal(i.capture_id)}>
                      Retry
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => {
                      if (confirm("Remove this photo from the phone before it uploads?")) {
                        void actions.discardLocal(i.capture_id);
                      }
                    }}
                  >
                    Remove
                  </button>
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {pending.length > 0 && (
        <section>
          <h3>On the server - the paper can go</h3>
          <ul class="qrows">
            {pending.map((c) => (
              <li key={c.capture_id} class="qrow">
                <ServerThumb captureId={c.capture_id} available={c.photo_available !== false} onView={setView} />
                <div class="qmain">
                  <span class="qtitle">{c.store}</span>
                  <span class="qsub muted small">
                    {when(c.captured_at)} · waiting for the next run
                  </span>
                  <TotalField
                    value={c.card_total}
                    currency={c.currency}
                    onSave={(v) => actions.setServerTotal(c.capture_id, v)}
                  />
                </div>
                <span class="qactions">
                  <button type="button" onClick={() => discardServer(c.capture_id)}>
                    Discard
                  </button>
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {held.length > 0 && (
        <section>
          <h3>Needs your answer</h3>
          {held.map((c) => {
            const open = c.answers.find((a) => !a.answer);
            return (
              <div class="card" key={c.capture_id}>
                <div class="qrow bare">
                  <ServerThumb captureId={c.capture_id} available={c.photo_available !== false} onView={setView} />
                  <div class="qmain">
                    <span class="qtitle">{c.store}</span>
                    <span class="qsub muted small">{when(c.captured_at)}</span>
                  </div>
                </div>
                <p>{open?.question ?? "This receipt could not be read."}</p>
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    const input = (e.target as HTMLFormElement).elements.namedItem(
                      "answer",
                    ) as HTMLInputElement;
                    if (!input.value) return;
                    void actions.answerServer(c.capture_id, input.value);
                  }}
                >
                  <input name="answer" placeholder="Your answer" />
                  <button type="submit">Send</button>
                </form>
                <button class="link" type="button" onClick={() => discardServer(c.capture_id)}>
                  Give up and discard
                </button>
              </div>
            );
          })}
        </section>
      )}

      {processed.length > 0 && (
        <section>
          <h3>In the ledger</h3>
          <ul class="qrows muted">
            {processed.map((c) => (
              <li key={c.capture_id} class="qrow">
                <ServerThumb captureId={c.capture_id} available={c.photo_available === true} onView={setView} />
                <div class="qmain">
                  <span class="qtitle">{c.store}</span>
                  <span class="qsub muted small">{when(c.captured_at)} · processed</span>
                </div>
              </li>
            ))}
          </ul>
          <p class="muted small">
            Processed photos are filed on your PC with the ledger; the server copy may be pruned.
          </p>
        </section>
      )}
    </div>
  );
}
